import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { UserContext } from "./UserContext";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast, Zoom } from 'react-toastify';
import './AddCustomerForm.css';
import FormHeader from '../FormHeader';

const AddCustomerForm = () => {
  const { user, token } = useContext(UserContext);
  const navigate = useNavigate();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  
  const role = user.role;
  
  const [form, setForm] = useState({
    username: '',
    password: '',
    name: '',
    lastName: '',
    MobileNumber: '',
    EmailId: '',
    address: '',
    operatorDetails:
      role === "operator"
        ? `${user.operatorName || ''} - ${user.phoneNumber || ''}`
        : role === "admin"
        ? `${user.operatorName || ''} - ${user.operatorPhoneNumber || ''}`
        : "",
    autoplay: 'on',
    operatorId:
      role === "operator"
        ? user.operatorId
        : role === "admin"
        ? user.operatorId
        : "",
    remarks: ''
  });

  const fetchOperators = async () => {
    setLoading(true)
    try {
      const response = await fetch(`https://vrplay.in/portal_php/operator_api.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();        
      console.log(data);
      
      if (response.ok) {
        setOperators(data.operators);
        setError(null);
      } else {
        setError(data.error || 'Failed to fetch operators');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (role !== "operator") {
      fetchOperators();
    }
  }, [role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...form, [name]: value };

    if (name === "MobileNumber") {
      updatedForm.username = value;
      updatedForm.password = value;
    }

    if (name === "operatorId") {
      const selectedId = Number(value);

      // Decide source list: for admin include self, for superadmin just API list
      const allOptions =
        role === "admin"
          ? [
              {
                id: Number(user.operatorId),
                operatorName: user.operatorName,
                phoneNumber: user.operatorPhoneNumber,
              },
              ...operators,
            ]
          : operators;

      const selectedOp = allOptions.find(op => op.id === selectedId);

      updatedForm = {
        ...updatedForm,
        operatorId: selectedId,
        operatorDetails: selectedOp
          ? `${selectedOp.operatorName} - ${selectedOp.phoneNumber}`
          : "",
      };
    }

    setForm(updatedForm);

    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // ✅ Validation function
  const validateForm = () => {
    let newErrors = {};
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-z\s]+$/; // only letters & spaces

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (!nameRegex.test(form.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    } else if (!nameRegex.test(form.lastName.trim())) {
      newErrors.lastName = "Last Name can only contain letters and spaces";
    }

    if (!form.MobileNumber) {
      newErrors.MobileNumber = "Phone is required";
    } else if (!phoneRegex.test(form.MobileNumber)) {
      newErrors.MobileNumber = "Phone must be 10 digits";
    }

    if (form.EmailId && !emailRegex.test(form.EmailId)) {
      newErrors.EmailId = "Invalid email format";
    }

    if (!form.operatorId && role !== "operator") {
      newErrors.operatorId = "Please select an operator";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors before submitting.");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    // ✅ Run validation first
    if (!validateForm()) return;
  
    try {
      await axios.post(
        'https://vrplay.in/portal_php/allCustomers.php',
        {
          ...form, // already has operatorId & operatorDetails correctly
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success('Customer added successfully!');
      navigate('/customers', { replace: true });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Customer already exists.');
      } else if (err.response?.status === 400) {
        toast.warn('Missing required fields.');
      } else {
        toast.error('Failed to add customer. Please try again later.');
      }
    }
  };

  return (
    <>
      <FormHeader />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Zoom}
        className="toast-mobile-optimized"
      />
      {loading ? (
          <div className="dashboard-loading">
            <div className="loader"></div>
            <p>Loading...</p>
          </div>
        ) : error ? (
          <p className="error">{error}</p>
        ) : (
          <div className={`form-container ${Object.keys(errors).length > 0 ? "form-container-error" : ""}`}>
            <h2>Add Customer</h2>
            <input name="name" placeholder="Name" onChange={handleChange} className={errors.name ? "error-input" : ""} />
            {errors.name && <p className="error-text">{errors.name}</p>}
            <input name="lastName" placeholder="Last Name" onChange={handleChange} className={errors.lastName ? "error-input" : ""} />
            {errors.lastName && <p className="error-text">{errors.lastName}</p>}
            
            {/* Phone Number with OTTPlay Check Button */}
            <input 
              name="MobileNumber" 
              placeholder="Phone" 
              onChange={handleChange} 
              className={errors.MobileNumber ? "error-input" : ""}
            />
            
            <input name="EmailId" placeholder="Email Id" onChange={handleChange} className={errors.EmailId ? "error-input" : ""} />
            {errors.EmailId && <p className="error-text">{errors.EmailId}</p>}
            <input name="address" placeholder="Address" onChange={handleChange} />
            
            {role !== "operator" ? (
              <>
                <select
                  name="operatorId"
                  value={form.operatorId}
                  className={errors.operatorId ? "error-input" : ""}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value);
                    const allOptions =
                      role === "admin"
                        ? [
                            {
                              id: Number(user.operatorId),
                              operatorName: user.operatorName,
                              phoneNumber: user.phoneNumber
                            },
                            ...operators
                          ]
                        : operators;

                    const selectedOp = allOptions.find(op => op.id === selectedId);

                    setForm({
                      ...form,
                      operatorId: selectedId,
                      operatorDetails: selectedOp
                        ? `${selectedOp.operatorName} - ${selectedOp.phoneNumber}`
                        : '',
                    });
                  }}
                >
                  <option value="">-- Select Operator/Admin --</option>
                  {role === "admin" && (
                    <option
                      key={`admin-${user.operatorId}`}
                      value={user.operatorId}
                    >
                      {user.operatorName} - {user.phoneNumber}
                    </option>
                  )}
                  {operators.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.operatorName} - {o.phoneNumber}
                    </option>
                  ))}
                </select>
                {errors.operatorId && <p className="error-text">{errors.operatorId}</p>}
              </>
            ) : (
              <input
                name="operatorDetails"
                placeholder="Operator Details"
                value={`${user.operatorName || ''} - ${user.phoneNumber || ''}`}
                readOnly
              />
            )}
            
            <select name="autoplay" value={form.autoplay} onChange={handleChange}>
              <option value="on">Autoplay On</option>
              <option value="off">Autoplay Off</option>
            </select>
            <input name="remarks" placeholder="Remarks" onChange={handleChange} />
            
            <div className="add-cancel">
              <button 
                onClick={handleSubmit}
              >
                Add Customer
              </button>
              <button className="cancel-btn" onClick={() => navigate('/customers', { replace: true })}>
                Cancel
              </button>
            </div>
          </div>
      )}
    </>
  );
};

export default AddCustomerForm;